// Drop-in replacement for the parts of the `query-string` package we use
// (parse / stringify / exclude / stringifyUrl), matching its defaults:
// repeated keys become arrays, keys without a value parse to null, keys are
// sorted, and values are strictly encoded. Decoding never throws on
// malformed percent-encoding and does a bounded amount of work per escape.

export type ParsedQuery = Record<string, string | null | (string | null)[]>;

type QueryPrimitive = string | number | boolean | null | undefined;
export type StringifiableQuery = Record<
  string,
  QueryPrimitive | QueryPrimitive[]
>;

// Longest UTF-8 sequence is 4 bytes, so try at most 4 escapes at a time.
const decodeEscapes = (run: string): string => {
  const tokens = run.match(/%[0-9a-f]{2}/gi) || [];
  let out = '';
  let i = 0;
  while (i < tokens.length) {
    let len = Math.min(4, tokens.length - i);
    for (; len > 0; len--) {
      try {
        out += decodeURIComponent(tokens.slice(i, i + len).join(''));
        break;
      } catch {
        // try a shorter sequence
      }
    }
    if (len === 0) {
      // query-string's decoder maps a stray `%C2` to U+FFFD; keep parity.
      out += tokens[i] === '%C2' ? '�' : tokens[i];
      i += 1;
    } else {
      i += len;
    }
  }
  return out;
};

const decode = (value: string): string => {
  const withSpaces = value.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(withSpaces);
  } catch {
    return withSpaces.replace(/(%[0-9a-f]{2})+/gi, decodeEscapes);
  }
};

const encode = (value: string | number | boolean): string =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );

const sortKeys = <T>(object: Record<string, T>): Record<string, T> =>
  Object.keys(object)
    .sort()
    .reduce((sorted, key) => {
      sorted[key] = object[key];
      return sorted;
    }, Object.create(null) as Record<string, T>);

const splitHash = (input: string): [string, string | undefined] => {
  const index = input.indexOf('#');
  return index === -1
    ? [input, undefined]
    : [input.slice(0, index), input.slice(index + 1)];
};

const extract = (input: string): string => {
  const [withoutHash] = splitHash(input);
  const index = withoutHash.indexOf('?');
  return index === -1 ? '' : withoutHash.slice(index + 1);
};

export const parse = (query: string): ParsedQuery => {
  const result: ParsedQuery = Object.create(null);
  const trimmed = (query || '').trim().replace(/^[?#&]/, '');
  if (!trimmed) {
    return result;
  }

  for (const param of trimmed.split('&')) {
    if (param === '') {
      continue;
    }
    const index = param.indexOf('=');
    const key = decode(index === -1 ? param : param.slice(0, index));
    const value = index === -1 ? null : decode(param.slice(index + 1));
    const existing = result[key];
    result[key] =
      existing === undefined
        ? value
        : ([] as (string | null)[]).concat(existing, value);
  }

  return sortKeys(result);
};

export const stringify = (object: StringifiableQuery): string =>
  Object.keys(object)
    .sort()
    .map((key) => {
      const value = object[key];
      if (value === undefined) {
        return '';
      }
      if (value === null) {
        return encode(key);
      }
      if (Array.isArray(value)) {
        return value
          .filter((item) => item !== undefined)
          .map((item) =>
            item === null ? encode(key) : `${encode(key)}=${encode(item)}`,
          )
          .join('&');
      }
      return `${encode(key)}=${encode(value)}`;
    })
    .filter((part) => part.length > 0)
    .join('&');

export const stringifyUrl = ({
  url,
  query,
}: {
  url: string;
  query: StringifiableQuery;
}): string => {
  const [withoutHash, hash] = splitHash(url);
  const base = withoutHash.split('?')[0];
  const search = stringify({ ...parse(extract(url)), ...query });
  return `${base}${search ? `?${search}` : ''}${
    hash === undefined ? '' : `#${hash}`
  }`;
};

export const exclude = (input: string, keys: string[]): string => {
  const [withoutHash, hash] = splitHash(input);
  const base = withoutHash.split('?')[0];
  const parsed = parse(extract(input));
  const kept: ParsedQuery = Object.create(null);
  for (const key of Object.keys(parsed)) {
    if (!keys.includes(key)) {
      kept[key] = parsed[key];
    }
  }
  const search = stringify(kept);
  return `${base}${search ? `?${search}` : ''}${
    hash ? `#${decode(hash)}` : ''
  }`;
};

export default { parse, stringify, stringifyUrl, exclude };
